import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import { isEmpty } from "lodash";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { enumsTypes } from "@shared/constants";
import { TicketTypeSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { ProductVariantSerializer } from "@app-contexts/public/features/products/data/types/products";
import { PublicMembershipLevelSerializer } from "@shared/modules/memberships/types";
import { recordAppError } from "@shared/utils/errors/errorLogger";

export interface ItemTypeWithUpgrades {
  ticketUpgradeOptions?: {
    id: number;
    name: string;
    price: number;
    priceDelta: number;
    itemData: TicketTypeSerializer;
  }[];
  membershipUpgradeOptions?: {
    id: number;
    name: string;
    price: number;
    priceDelta: number;
    itemData: PublicMembershipLevelSerializer;
  }[];
  productUpgradeOptions?: {
    id: number;
    name: string;
    price: number;
    priceDelta: number;
    itemData: ProductVariantSerializer;
  }[];
}

export interface BasketUpgradeOption {
  id: number;
  name: string;
  price: number;
  priceDelta: number;
  messageTemplate?: string | null;
  targetItemType: enumsTypes.ItemTypesMapIdType;
  itemData:
    | TicketTypeSerializer
    | PublicMembershipLevelSerializer
    | ProductVariantSerializer;
}

export interface BasketUpgradeInfo {
  itemId: number;
  itemType: enumsTypes.ItemTypesMapIdType;
  currency: enumsTypes.CurrencyMapIdType;
  upgradeOptions?: BasketUpgradeOption[] | null;
}

export interface BasketUpgradesResult {
  [itemGroupId: string]: BasketUpgradeInfo;
}

export type TicketTypeWithUpgrades = TicketTypeSerializer & {
  ticket_upgrade_options?: {
    id: number;
    name: string;
    price: string;
    upgrade_message_template?: string | null;
  }[];
};

export type MembershipLevelWithUpgrades = PublicMembershipLevelSerializer & {
  membership_upgrade_options?: {
    id: number;
    name: string;
    price: string;
    upgrade_message_template?: string | null;
  }[];
};

export type ProductVariantWithUpgrades = ProductVariantSerializer & {
  product_upgrade_options?: {
    id: number;
    name: string;
    price: string;
  }[];
};
export interface ItemTypeMappings {
  ticketTypesMapping: Record<string, TicketTypeSerializer>;
  membershipLevelsMapping: Record<string, PublicMembershipLevelSerializer>;
  productsAttributesMapping: Record<string, ProductVariantSerializer>;
}

export interface TicketTypesProvider {
  getItemTypeMappings(): ItemTypeMappings;
}

/**
 * Domain service for handling basket upgrades business logic
 * This service is responsible for formatting eligible upgrades for item groups in a basket.
 * Supports tickets, memberships, and products.
 */
export class BasketUpgradesService {
  private itemTypesProvider?: TicketTypesProvider;

  constructor(itemTypesProvider?: TicketTypesProvider) {
    this.itemTypesProvider = itemTypesProvider;
  }

  private static getUpgradeInfoForItemGroup(
    ig: TicketItemGroupType,
    mappings: ItemTypeMappings,
    currency: enumsTypes.CurrencyMapIdType
  ): BasketUpgradeInfo | undefined {
    if (!ig.item_type) {
      recordAppError(new Error("Item type is undefined"));
      return undefined;
    }

    if (
      (ig.tt_seat_permissions && ig.tt_seat_permissions.length > 0) ||
      (ig.tt_location_permissions && ig.tt_location_permissions.length > 0)
    ) {
      return undefined;
    }

    const getItemId = (ig: TicketItemGroupType) => {
      switch (ig.item_type) {
        case ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
          return ig.type;
        case ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
          return ig.membership_level;
        case ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
          return ig.product_attribute;
        default:
          return undefined;
      }
    };

    const getUpgradeOptions = (ig: TicketItemGroupType) => {
      switch (ig.item_type) {
        case ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
          return BasketUpgradesService.getTicketTypeUpgrades(ig, mappings);
        case ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
          return BasketUpgradesService.getMembershipLevelUpgrades(ig, mappings);
        case ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
          // TODO: Implement product attribute upgrades
          return null;
        default:
          return null;
      }
    };

    const itemId = getItemId(ig);

    return {
      itemId,
      itemType: ig.item_type,
      currency,
      upgradeOptions: getUpgradeOptions(ig),
    };
  }

  private static toUpgradeOption(
    upgradeOption: {
      id: number;
      name: string;
      price?: string;
      upgrade_message_template?: string | null;
    },
    originalPrice: string | undefined,
    targetItemType: enumsTypes.ItemTypesMapIdType
  ): BasketUpgradeOption {
    return {
      id: upgradeOption.id,
      name: upgradeOption.name,
      price: Number(upgradeOption.price ?? 0),
      priceDelta: Number(upgradeOption.price ?? 0) - Number(originalPrice ?? 0),
      messageTemplate: upgradeOption.upgrade_message_template,
      targetItemType,
      itemData: upgradeOption,
    };
  }

  private static getTicketTypeUpgrades(
    ig: TicketItemGroupType,
    mappings: ItemTypeMappings
  ): BasketUpgradeOption[] | null {
    if (!mappings.ticketTypesMapping || !ig.event || !ig.type) {
      return null;
    }

    const ticketType = mappings.ticketTypesMapping[`${ig.event}:${ig.type}`];
    if (!ticketType) {
      return null;
    }

    if (ticketType.seat_permissions && ticketType.seat_permissions.length > 0) {
      return null;
    }

    const originalPrice = ticketType.price;

    const ticketUpgrades: BasketUpgradeOption[] =
      ticketType.ticket_upgrade_options?.map((opt) =>
        BasketUpgradesService.toUpgradeOption(
          opt,
          originalPrice,
          ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
        )
      ) ?? [];

    const membershipUpgrades: BasketUpgradeOption[] =
      ticketType.membership_upgrade_options?.map((opt) =>
        BasketUpgradesService.toUpgradeOption(
          opt,
          originalPrice,
          ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
        )
      ) ?? [];

    const allUpgrades = [...ticketUpgrades, ...membershipUpgrades];

    const upgradesWithoutSeating = allUpgrades.filter((opt) => {
      if (opt.targetItemType !== ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id)
        return true;
      const targetTT = mappings.ticketTypesMapping[`${ig.event}:${opt.id}`];
      return !(
        targetTT?.seat_permissions && targetTT.seat_permissions.length > 0
      );
    });

    return upgradesWithoutSeating.length > 0 ? upgradesWithoutSeating : null;
  }

  // TODO: Restrict upgrades for membership levels with assigned seating.
  // PublicMembershipLevelSerializer does not include seat_permissions and
  // membershipGroupsMapping (which has reserved_seating_benefits) is not
  // available in this service. Once that data is passed through, filter
  // out destination membership levels that require assigned seating.
  private static getMembershipLevelUpgrades(
    ig: TicketItemGroupType,
    mappings: ItemTypeMappings
  ): BasketUpgradeOption[] | null {
    if (
      !mappings.membershipLevelsMapping ||
      !ig.membership_group ||
      !ig.membership_level
    ) {
      return null;
    }

    const membershipLevel =
      mappings.membershipLevelsMapping[
        `${ig.membership_group}:${ig.membership_level}`
      ];
    if (!membershipLevel) {
      return null;
    }

    const originalPrice = membershipLevel.price;
    const upgradeOptions: BasketUpgradeOption[] =
      membershipLevel.membership_upgrade_options?.map((opt) =>
        BasketUpgradesService.toUpgradeOption(
          opt,
          originalPrice,
          ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
        )
      ) ?? [];
    return upgradeOptions.length > 0 ? upgradeOptions : null;
  }

  /**
   * Get upgrades for all item groups in a basket
   */
  public getBasketUpgrades(
    itemGroups: TicketItemGroupType[],
    itemTypeMappings: ItemTypeMappings,
    currency: enumsTypes.CurrencyMapIdType
  ): BasketUpgradesResult | undefined {
    const mappings = itemTypeMappings || this.getItemTypeMappings();

    if (!itemGroups || isEmpty(mappings) || !currency) {
      return undefined;
    }

    const itemGroupsMapping = itemGroups.reduce(
      (acc: BasketUpgradesResult, ig: TicketItemGroupType) => {
        if (ig.id !== undefined) {
          const upgradeInfo = BasketUpgradesService.getUpgradeInfoForItemGroup(
            ig,
            mappings,
            currency
          );
          if (upgradeInfo) {
            acc[ig.id] = upgradeInfo;
          }
        }
        return acc;
      },
      {}
    );

    return itemGroupsMapping;
  }

  private getUpgradeForItemGroup(
    itemGroup: TicketItemGroupType,
    itemTypeMappings: ItemTypeMappings,
    currency: enumsTypes.CurrencyMapIdType
  ): BasketUpgradeInfo | undefined {
    const mappings = itemTypeMappings || this.getItemTypeMappings();

    if (!itemGroup || isEmpty(mappings) || !currency) {
      return undefined;
    }

    return BasketUpgradesService.getUpgradeInfoForItemGroup(
      itemGroup,
      mappings,
      currency
    );
  }

  public getUpgradeForItemGroupById(
    itemGroupId: number,
    itemGroups: TicketItemGroupType[],
    itemTypeMappings: ItemTypeMappings,
    currency: enumsTypes.CurrencyMapIdType
  ): BasketUpgradeInfo | undefined {
    const itemGroup = itemGroups.find((ig) => ig.id === itemGroupId);
    if (!itemGroup) {
      return undefined;
    }

    return this.getUpgradeForItemGroup(itemGroup, itemTypeMappings, currency);
  }

  private getItemTypeMappings(): ItemTypeMappings | undefined {
    return this.itemTypesProvider?.getItemTypeMappings();
  }
}

export const basketUpgradesService = new BasketUpgradesService();
